import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid, IconButton, Toolbar, Typography, Box } from "@mui/material/";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import makeStyles from "@mui/styles/makeStyles";
import actions from "../../../Store/actions";
import { strings } from "../../../common/i18n";
import styles from "./styles";

type Props = {
    Actions: React.ReactNode,
    onNext: () => void,
    onPrevious: () => void,
    value: any,
    values: Array<number>
};

/**
 * Audit Review Toolbar Component
 */
export default ({ Actions, onNext, onPrevious, value, values }: Props) => {
    const { auditReview, appSettings } = useSelector((state) => state);
    const { appBarTitle } = appSettings;
    const navigate = useNavigate();
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();

    const index = useMemo(() => {
        if (value && values.length) {
            return values.indexOf(value);
        }
        return 0;
    }, [value, values]);

    const onBackClick = () => {
        dispatch(actions.auditReview.create.statusSet(auditReview.status));
        navigate(`..`);
    };

    const navDisabled = values.length === 0;

    return (
        <Toolbar className={classes.toolbar} disableGutters>
            <Grid container alignItems="center">
                {/* Left: Text Section */}
                <Grid item xs={4}>
                    <Box display="flex" flexDirection="column">
                        <Typography variant="body1" fontWeight="medium">
                            {strings("reviewAudit")}
                        </Typography>
                        <Typography
                            className={classes.toolbarSubHeading}
                            variant="caption"
                            color="textSecondary"
                            title={appBarTitle}
                            noWrap
                        >
                            {appBarTitle}
                        </Typography>
                    </Box>
                </Grid>

                {/* Center: Navigation and Count */}
                <Grid item xs={4}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        height="100%"
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <IconButton
                                disabled={navDisabled}
                                onClick={onPrevious}
                                size="medium"
                                aria-label="Previous"
                            >
                                <ChevronLeft />
                            </IconButton>

                            <Typography
                                className={classes.toolbarProgressText}
                                variant="body2"
                                align="center"
                                minWidth={100}
                            >
                                {strings("asset")}{" "}
                                <Typography component="span">
                                    {index + 1}
                                </Typography>
                                {" of "}
                                <Typography component="span">
                                    {values.length || 1}
                                </Typography>
                            </Typography>

                            <IconButton
                                disabled={navDisabled}
                                onClick={onNext}
                                size="medium"
                                aria-label="Next"
                            >
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Box>
                </Grid>

                {/* Right: Close Button */}
                <Grid item xs={4}>
                    <Box display="flex" justifyContent="flex-end">
                        <IconButton
                            onClick={onBackClick}
                            title="Close"
                            size="medium"
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>
        </Toolbar>
    );
};
